import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  signal,
} from '@angular/core';
import { MainService } from '../../../../core/common/services/main/main.service';
import { ClickOutSideDirective } from '../../../directives/clickOutSide.directive';
import { ModalServiceService } from '../../modal/modal-service.service';
import { MenuQuickAction } from '../../sidenav/sidenav.model';
import { SideMenuIconComponent } from '../side-menu-icon/side-menu-icon.component';

/**
 * The ＋ at the end of a side-menu row (Figma's quick add). Opens the row's quick-action panel
 * beside the menu, level with the button. The panel is fixed-positioned from the button because the nav scrolls,
 * and a scroll box clips anything absolutely positioned inside it.
 */
@Component({
  selector: 'lib-side-menu-quick-action',
  imports: [NgTemplateOutlet, ClickOutSideDirective, SideMenuIconComponent],
  templateUrl: './side-menu-quick-action.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SideMenuQuickActionComponent {
  private readonly mainService = inject(MainService);
  private readonly modalService = inject(ModalServiceService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  /** The row's key; one quick-action panel is open at a time across the menu. */
  readonly key = input.required<string>();
  readonly action = input.required<MenuQuickAction>();

  private readonly overlayKey = computed(
    () => `quick-action:${this.key()}` as const
  );
  protected readonly isOpen = computed(
    () => this.mainService.openOverlay() === this.overlayKey()
  );
  protected readonly panelTop = signal(0);
  protected readonly panelStart = signal(0);

  toggle(): void {
    if (this.isOpen()) {
      this.mainService.openOverlay.set(null);
      return;
    }
    // Level with the button, just outside the menu's edge (inline-start is right in RTL).
    const host = this.host.nativeElement;
    const edge = (host.closest('aside') ?? host).getBoundingClientRect();
    const rtl = document.dir === 'rtl';
    this.panelTop.set(host.getBoundingClientRect().top);
    this.panelStart.set(
      (rtl ? window.innerWidth - edge.left : edge.right) + PANEL_GAP_PX
    );
    this.mainService.openOverlay.set(this.overlayKey());
  }

  /** A quick action can open a modal (e.g. request settings); clicks inside it must not close the panel. */
  closeOnOutsideClick(): void {
    if (this.isOpen() && !this.modalService.modalContent().isOpen) {
      this.mainService.openOverlay.set(null);
    }
  }
}

const PANEL_GAP_PX = 8;
